import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { NormalizedGroup } from '../../../redux/groups/api/GroupAdapter';
import { BusinessRole } from '../redux/businessModel/model/model';
import { GroupAPI } from '../../../redux/groups/api/GroupAPI';

interface UseGroupManagerDataParams {
  isSccCustomer: boolean;
  groups: NormalizedGroup[] | undefined;
  companiesGroups: Record<string, NormalizedGroup>;
}

interface UseGroupManagerDataReturn {
  storeGroups: NormalizedGroup[];
  companyGroups: NormalizedGroup[];
  childStoreGroups: NormalizedGroup[];
  isLoadingChildGroups: boolean;
}

/**
 * Custom hook to manage group data filtering and loading based on user role
 * Handles:
 * - Auto-navigation for SCC customers to their company group
 * - Loading child groups (stores) when a company is selected
 * - Filtering and formatting groups with appropriate icons
 */
export const useGroupManagerData = ({
  isSccCustomer,
  groups,
  companiesGroups,
}: UseGroupManagerDataParams): UseGroupManagerDataReturn => {
  const navigate = useNavigate();
  const params = useParams<{ groupId: string }>();
  const [getGroupById] = GroupAPI.useLazyGetGroupByIdQuery();
  const [childStoreGroups, setChildStoreGroups] = useState<NormalizedGroup[]>(
    []
  );
  const [isLoadingChildGroups, setIsLoadingChildGroups] = useState(false);

  /**
   * Auto-redirect SCC customers (store users) to their company group
   */
  useEffect(() => {
    if (isSccCustomer && groups) {
      const companyGroup = groups.find(
        (group) => group.attributes?.['BUSINESS_ROLE'] === BusinessRole.COMPANY
      );
      if (companyGroup && !params.groupId) {
        navigate(`group/${companyGroup.id}`);
      }
    }
  }, [isSccCustomer, navigate, groups, params.groupId]);

  /**
   * Load child groups (stores) when a company is selected
   * Only for company users viewing a specific company
   */
  useEffect(() => {
    // Skip for SCC customers or when no group is selected
    if (isSccCustomer || !params.groupId) {
      setChildStoreGroups([]);
      setIsLoadingChildGroups(false);
      return;
    }

    const groupSelected = companiesGroups[params.groupId];
    if (!groupSelected?.childGroups || groupSelected.childGroups.length === 0) {
      setChildStoreGroups([]);
      setIsLoadingChildGroups(false);
      return;
    }

    let isCancelled = false;

    const loadChildGroups = async () => {
      setIsLoadingChildGroups(true);
      const loadedGroups: NormalizedGroup[] = [];

      try {
        for (const child of groupSelected.childGroups) {
          if (isCancelled) break;

          const newGroup = await getGroupById(child).unwrap();
          if (newGroup && !isCancelled) {
            loadedGroups.push(newGroup);
          }
        }

        if (!isCancelled) {
          setChildStoreGroups(loadedGroups);
          setIsLoadingChildGroups(false);
        }
      } catch (error) {
        console.error('Error loading child groups:', error);
        if (!isCancelled) {
          setChildStoreGroups([]);
          setIsLoadingChildGroups(false);
        }
      }
    };

    loadChildGroups();

    return () => {
      isCancelled = true;
    };
  }, [isSccCustomer, params.groupId, companiesGroups, getGroupById]);

  /**
   * Filter groups by role
   */
  const storeGroups = useMemo(() => {
    return (
      groups?.filter(
        (group: NormalizedGroup) =>
          group.attributes?.['BUSINESS_ROLE'] === BusinessRole.STORE
      ) || []
    );
  }, [groups]);

  const companyGroups = useMemo(() => {
    return Object.values(companiesGroups || {}).filter(
      (group: NormalizedGroup) =>
        group.attributes?.['BUSINESS_ROLE'] === BusinessRole.COMPANY
    );
  }, [companiesGroups]);

  return {
    storeGroups,
    companyGroups,
    childStoreGroups,
    isLoadingChildGroups,
  };
};
